import React, { Component } from 'react';
import {Link} from 'react-router-dom';
import examples from '../../../utils/examples';

import HttpRunner from '../query';
import EntityIdQuery from '../entityIdQuery';
import urlHandler from '../../../utils/urlHandler';
import miscUtils from '../../../utils/miscUtils';

class DocCustomEntities extends Component {
    constructor(props){
        super(props);
    }

    render() {
        return (
            <div className="bucket" id="top">
                <h3 className="heading-1">Custom RegEx Fields</h3>
                <div className="description">
                    The Custom RegEx Web API uses a very specific schema with the importance that we use the same validation, normalization, and tagging. This ensures all properties are executed appropriately.
                    <br/><br/>
                    This API is a bit more complicated than the Custom Entity API as it uses RegEx patterns that follow the <a href="https://github.com/google/re2/wiki/Syntax">RE2 Syntax</a> which may require some additional testing before updating in the system. An invalid regular expression can cause issues downstream.                    
                </div>
                <br/>
                Below is an overview of the general schema required for any and all of the following Custom RegEx endpoints:<br/>

                <Link to="/documentation/doc_v1regexcreate#top">POST - /api/v1/regex/insert</Link><br/>
                <Link to="/documentation/doc_v1regexvalidate#top">POST - /api/v1/regex/validate</Link><br/>
                <Link to="/documentation/doc_v1regexdelete#top">DELETE - /api/v1/regex/delete</Link>
                <br/>
                <h4 className="heading-2">Custom Regex</h4>

                <h4 className="heading-3" id="custom_regex_object">Custom Regex Object</h4>
                <table className="table-wrapper enrich">
                    <thead className="table-header enrich">
                    <tr>
                        <th>Name </th>
                        <th>Description </th>
                        <th>Values </th>
                        <th>Default </th>
                    </tr>
                    </thead>

                    <tbody className="table-body">
                    <tr>
                        <td className="attribute">entries</td>
                        <td className="meaning">A list of JSON entry objects to ADD or REMOVE tags from the output.</td>
                        <td className="type">entry[]</td>
                        <td className="type">Required</td>
                    </tr>
                    </tbody>
                </table>
                <br/>
                <h4 className="heading-2" id="custom_regex_entry_object">Entries</h4>
                <h4 className="heading-3">Entry Object</h4>
                    The entries JSON array contains a list of pattern configurations. Here are the required elements for specifying a particular pattern entry in the list.                
                <table className="table-wrapper enrich">
                    <thead className="table-header enrich">
                    <tr>
                        <th>Name </th>
                        <th>Description </th>
                        <th>Values </th>
                        <th>Default </th>
                    </tr>
                    </thead>

                    <tbody className="table-body">
                    <tr>
                        <td className="attribute">patternName</td>
                        <td className="meaning">The name of the pattern. This name is NOT the entity type and this name MUST be unique from other pattern names.
                        </td>
                        <td className="type">String</td>
                        <td className="constraints">Required</td>
                    </tr>
                    <tr>
                        <td className="attribute">entityType</td>
                        <td className="meaning">This will be the type of the entity in the enrich output from Finch for Text.
                        </td>
                        <td className="type">String</td>
                        <td className="constraints">Required</td>
                    </tr>
                    <tr>
                        <td className="attribute">description</td>
                        <td className="meaning">A descriptive note for the user that is never exposed in the enriched output.
                        </td>
                        <td className="type">String</td>
                        <td className="constraints">Optional</td>
                    </tr>
                    <tr>
                        <td className="attribute">extractionConfidence</td>
                        <td className="meaning">
                            The static extraction score for any match from this pattern.<br/>
                            Entities with higher confidence will effectively overide those with lower confidence.

                        </td>
                        <td className="type">Decimal<br/><br/>
                        Bounding Range: [0.0 - 1.0] </td>
                        <td className="type">Required</td>
                    </tr>
                    <tr>
                        <td className="attribute">pattern</td>
                        <td className="meaning">The regular expression pattern to match. This must meet RE2 standards (<a href="https://github.com/google/re2/wiki/Syntax">RE2 Syntax</a>).  Any named captured groups will be added as attributes in the output.</td>
                        <td className="type">String</td>
                        <td className="constraints">Required</td>
                    </tr>
                    <tr>
                        <td className="attribute">removeAllOverlap</td>
                        <td className="meaning">Specifies to remove any other entity that overlaps with matches from the user defined pattern. By default, this feature is false.</td>
                        <td className="type">Boolean</td>
                        <td className="constraints">Optional</td>
                    </tr>
                    <tr>
                        <td className="attribute">metadata</td>
                        <td className="meaning">Represents static attributes associated with any and all matches for the pattern. The JSON object is parsed and added as additional attributes for each match from this pattern.
                        </td>
                        <td className="type">JsonObject</td>
                        <td className="constraints">Optional</td>
                    </tr>
                    </tbody>
                </table>
                <br/>

                <h4 className="heading-2" id="custom_regex_examples">Examples</h4>
                <h4 className="heading-3">Basic</h4>
                The "ssn" entry below uses a basic RE2 pattern to find social security numbers.<br/>
                The pattern uses a regular expression to match three digits, a hyphen, two more digits, another hyphen, and finally four digits
                <br/>
                <h4 className="heading-3">Basic Example</h4>
                    <div className="example-content host">
                        <pre>
                            {JSON.stringify(examples.EXAMPLE_BASIC_REGEX, null, 3)}
                        </pre>
                    </div>       
                <br/>
                <h4 className="heading-3">Advanced Patterns</h4>
                An advanced use is to use captured named groups to identify attributes that should be displayed in the Finch for Text output structure.<br/>
                Named groups are represented with ?P&lt;groupName&gt; like such: (?P&lt;fooBar&gt;x|y|z). <br/>
                This captures the text matched by "x", "y", or "z" into the group "fooBar".<br/>
                The name can contain letters and numbers but must start with a letter and must be unique. No two named groups can share a name. <br/>
                These named groups will be translated into attributes (as Strings only) in the final output.<br/>
                <br/>
                The "trending_hashtag" entry below uses a captured named group "tag" to extract strings starting with a #. <br/>
                The captured  string is then stored it in the "tag" attribute of the Finch for Text output structure.<br/>
                <h4 className="heading-3">Advanced Example</h4>
                    <div className="example-content host">
                        <pre>
                            {JSON.stringify(examples.EXAMPLE_ADVANCED_REGEX, null, 3)}
                        </pre>
                    </div>       
                <br/>     
            </div>
        );
    }
}

export default DocCustomEntities;